import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  finalize,
  map,
  takeUntil,
  tap
} from 'rxjs/operators';
import { parseISO } from 'date-fns';

import { COMMON } from '@sondermind/constants';
import {
  AUStateNameMap,
  SupportedCountries,
  SupportedCountryMap,
  USStateNameMap
} from '@sondermind/utilities/states-list';
import { CurrentUser } from '@sondermind/data-access/client-user';
import { UserDataService } from '@sondermind/data-access/client-facing/user-data';
import { ReferenceDataHttpService, ReferenceDataTypes } from '@sondermind/data-access/reference-data';
import { ServiceError } from '@sondermind/utilities/models-http';
import { SharedMasks, TextMasks } from '@sondermind/utilities/text-masks';

import { LocalizedDateService, toTimeZoneDateTime } from '@sondermind/utilities/time-and-date';
import { ClientUserProfile } from '@sondermind/utilities/models-user-client';

import {
  IrisButtonContainerAlignments,
  IrisButtonTypes
} from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { CommunicationPreference } from '@sondermind/utilities/models-clinical';
import { BirthdayValidator } from '@sondermind/utilities/forms';
import { ProviderStore } from '@sondermind/data-access/client-portal-referrals';
import { NotificationToastService, ToastPosition, ToastType } from '@sondermind/features/notification-toast';

interface IIrisDropdownOption {
  name: string;
  value: string | number;
}

interface ICommunicationPreferencesFormControls {
  commPrefsPhone: FormControl<boolean | null>;
  commPrefsSms: FormControl<boolean | null>;
}

export interface IProfileEditFormControls {
  id: FormControl<string | number | null>;
  userId: FormControl<string | number | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  preferredFirstName: FormControl<string | null>;
  birthDate: FormControl<string | null>;
  phoneNumber: FormControl<string | null>;
  mailingCountry: FormControl<string | null>;
  mailingAddressLine1: FormControl<string | null>;
  mailingAddressLine2: FormControl<string | null>;
  mailingCity: FormControl<string | null>;
  mailingState: FormControl<string | null>;
  mailingPostalCode: FormControl<string | null>;
  timeZone: FormControl<string | null>;
  genderId: FormControl<string | number | null>;
  verification: FormControl<boolean | null>;
  commPrefs: FormGroup<ICommunicationPreferencesFormControls>;
}
@Component({
  selector: 'client-profile-edit-form',
  templateUrl: './profile-edit-form.component.html',
  styleUrls: ['./profile-edit-form.component.scss'],
  standalone: false
})
export class ProfileEditFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() useInSessionRequest: boolean = false;
  @Input() accountComplete: boolean = false;
  @Input() isProposeATime: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() fromDirectBooking: boolean = false;
  @Output() basicInfoFirstSubmit: EventEmitter<void> = new EventEmitter<void>();

  private destroyed$ = new Subject<void>();

  irisButtonContainerAlignments = IrisButtonContainerAlignments;
  irisButtonTypes = IrisButtonTypes;
  irisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;

  constructor(
    private currentUserService: CurrentUser,
    private referenceDataHttpService: ReferenceDataHttpService,
    private formBuilder: FormBuilder,
    private notificationToastService: NotificationToastService,
    private userDataService: UserDataService,
    private localizedDateService: LocalizedDateService,
    // NOTE: USED FOR PROJECT JUNO
    private providerStore: ProviderStore,
  ) {}

  masks: TextMasks = SharedMasks;
  zipcodeMask = this.masks['zipcodeMask'];
  phoneNumberMask = this.masks['phoneMask'];

  readonly stateOptions = signal<Array<{ name: string; value: string; }>>(USStateNameMap);
  supportedCountryOptions = SupportedCountryMap;
  profileForm: FormGroup<Partial<IProfileEditFormControls>> = new FormGroup({});
  dateToday: Date = new Date();
  maxDate: string = `${this.dateToday.getFullYear()}-${
    String(this.dateToday.getMonth() + 1).padStart(2, '0')
  }-${
    String(this.dateToday.getDate()).padStart(2, '0')
  }`;
  error: string | null = null;
  submitting: boolean = false;
  accountStatus: string;
  supportPhoneLink = COMMON.SUPPORT_PHONE_NO_MATCH_LINK;
  supportPhoneDisplay = COMMON.SUPPORT_PHONE_NO_MATCH_DISPLAY;
  supportEmail = COMMON.SUPPORT_EMAIL;
  // used to determine when all fields are needed, such as if data did not carry over from match flow
  useFullForm: boolean = false;
  mobileBday: string;

  profile$: Observable<ClientUserProfile | null> = of(null);
  gender$: Observable<string>;
  formReady$ = new BehaviorSubject<boolean>(false);

  genderOptions: IIrisDropdownOption[] = [];

  genderOptions$: Observable<IIrisDropdownOption[]>;

  // JUNO: We add an additional field for clients to select their country
  // first if they are saas-invited (SAAS clients can be outside of the US)
  readonly saasInvited = signal<boolean>(false);
  saasSupportEmail = COMMON.SAAS_SUPPORT_EMAIL;
  readonly addressFieldsDisabled = signal<boolean>(false);

  skipAcknowledgeInfo: boolean = false;

  ngOnInit(): void {
    this.providerStore.isClientSaasInvited$().pipe(takeUntil(this.destroyed$)).subscribe(
      (saasInvited) => {
        this.saasInvited.set(saasInvited);

        this.profileDataInit();
        this.profileInit();

        this.accountStatus = this.accountComplete ? 'updating' : 'completing';
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isMobile'] && changes['isMobile'].currentValue !== changes['isMobile'].previousValue) {
      this.setBday();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Submit the user's profile form.
   * If the account is complete, refresh the profile and reset the form.
   */
  submitForm(): void {
    this.startSubmitting();
    const birthDateString = this.profileForm.value.birthDate?.toString() || '';
    const bday = this.toIsoDateString(birthDateString);
    const commPrefs = this.getCommPrefsArray(this.profileForm.value.commPrefs || {});

    const formValue = this.profileForm.value;
    const profileData: Partial<ClientUserProfile> = {
      ...formValue,
      id: typeof formValue.id === 'string' ? parseInt(formValue.id, 10) : formValue.id,
      userId: typeof formValue.userId === 'string' ? parseInt(formValue.userId, 10) : formValue.userId,
      genderId: typeof formValue.genderId === 'string' ? parseInt(formValue.genderId, 10) : formValue.genderId,
      birthDate: bday,
      communicationPreferences: commPrefs
    };

    this.userDataService.updateProfile(profileData, { verify: true }).pipe(
      finalize(() => this.stopSubmitting())
    ).subscribe({
      next: (userProfile: ClientUserProfile) => {
        this.error = null;

        if (this.accountComplete) {
          this.notificationToastService.showNotificationToast({
            message: 'Profile saved',
            autoClose: true,
            type: ToastType.SUCCESS,
            position: ToastPosition.BOTTOM_CENTER
          });
          this.currentUserService.refreshProfile(userProfile);
          this.profileForm.reset(this.profileForm.value);
          this.addressFieldsDisabled.set(false);
          this.toggleAddressFields(false);
        } else {
          // if used by incomplete account, tell parent form submit is complete
          this.basicInfoFirstSubmit.emit();
        }
      },
      error: (err) => {
        this.setErrorMessage(err);
      }
    });
  }

  parseDateString(dateString: string, timeZone: string): Date {
    return toTimeZoneDateTime(parseISO(this.toIsoDateString(dateString)), timeZone).toJSDate();
  }

  getCommPrefsArray(commPrefs: Partial<{
    commPrefsPhone: boolean | null;
    commPrefsSms: boolean | null;
  }>): string[] {
    const prefs: CommunicationPreference[] = [CommunicationPreference.NCC];
    if (commPrefs.commPrefsPhone) prefs.push(CommunicationPreference.PHONE);
    if (commPrefs.commPrefsSms) prefs.push(CommunicationPreference.SMS);
    return prefs;
  }

  /**
    * In mobile, we swap the datepicker to one that returns values in mm/dd/yyyy format.
    * Rather than assuming the datestring is in mm/dd/yyyy format when isMobile = true,
    * check the format via regex to account for when:
    *   - the value is unchanged (i.e., user.profile.birth_date was already set),
    *   - the viewport changes from desktop to mobile and vice versa.
    */
  toIsoDateString(dateString: string): string {
    const isMmDdYyyy = /^\d{2}\/\d{2}\/\d{4}$/.test(dateString);
    if (isMmDdYyyy) {
      const [month, day, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }

    return dateString;
  }

  /**
   * Initialize all observables related to the user's profile edit form.
   */
  profileDataInit(): void {
    const filterAndSort = {
      filter: [{ key: 'hidden', value: 'false' }],
      sort: [{ key: 'order_number', asc: true }]
    };

    this.gender$ = this.profile$.pipe(map((p) => (p ? p.gender || 'na' : null)));
    this.genderOptions$ = this.referenceDataHttpService.list(
      ReferenceDataTypes.genders,
      filterAndSort
    ).pipe(
      map((response) => response.data),
      map((genders) => genders.map((gender) => ({
        name: gender.title,
        value: gender.id
      })))
    );
  }
  /**
   * Initialize the user profile observable, then build the edit form.
   */
  profileInit(): void {
    this.useFullForm = this.accountComplete;

    // Now these observables are guaranteed to be initialized
    this.profile$ = combineLatest({
      user: this.currentUserService.user$,
      genderOptions: this.genderOptions$,
    }).pipe(
      tap(({ genderOptions, user }) => {
        this.genderOptions = genderOptions;
        this.skipAcknowledgeInfo = user?.skipAcknowledgeInfo ?? false;
      }),
      map(({ user }) => (user ? user.profile : null))
    );

    this.profile$.subscribe((profile) => {
      if (profile) {
        if (!this.accountComplete) {
          this.checkEmptyRequiredFields(profile);
        }
        this.profileForm = this.buildForm(profile);
        this.setBday();
        this.formReady$.next(true);

        this.subscribeToCountryFieldUpdates();
      }
    });
  }

  /**
   * Build user data form, remove redundant match flow fields if account is incomplete.
   */
  private buildForm(profile: ClientUserProfile): FormGroup<Partial<IProfileEditFormControls>> {
    const formGroup = this.formBuilder.group<Partial<IProfileEditFormControls>>({
      id: new FormControl<string | number | null>(profile.id, {
        validators: Validators.required,
        nonNullable: false
      }),
      userId: new FormControl<string | number | null>(this.currentUserService.user?.id || null, {
        nonNullable: false
      }),
      firstName: new FormControl<string | null>(profile.firstName, {
        validators: Validators.required,
        nonNullable: false
      }),
      lastName: new FormControl<string | null>(profile.lastName, {
        validators: Validators.required,
        nonNullable: false
      }),
      preferredFirstName: new FormControl<string | null>(profile.preferredFirstName, {
        nonNullable: false
      }),
      birthDate: new FormControl<string | null>(profile.birthDate, {
        validators: [Validators.required, BirthdayValidator.validate],
        nonNullable: false
      }),
      phoneNumber: new FormControl<string | null>(profile.phoneNumber, {
        validators: Validators.required,
        nonNullable: false
      }),
      mailingCountry: new FormControl<string | null>(profile.mailingAddress.country, {
        nonNullable: false
      }),
      mailingAddressLine1: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.addressLine1,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false,
        }
      ),
      mailingAddressLine2: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.addressLine2,
          disabled: this.addressFieldsDisabled()
        },
        {
          nonNullable: false
        }
      ),
      mailingCity: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.city,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false
        }
      ),
      mailingState: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.state,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false
        }
      ),
      mailingPostalCode: new FormControl<string | null>(
        {
          value: profile.mailingAddress?.postalCode,
          disabled: this.addressFieldsDisabled()
        },
        {
          validators: Validators.required,
          nonNullable: false
        }
      ),
      timeZone: new FormControl<string | null>(
        {
          value: this.localizedDateService.getBrowserTimezone(),
          disabled: true,
        },
        {
          validators: Validators.required,
          nonNullable: false,
        }
      ),
      genderId: new FormControl<string | number | null>(profile.genderId, {
        validators: Validators.required,
        nonNullable: false
      }),
      verification: new FormControl<boolean | null>(profile.verification, {
        validators: this.skipAcknowledgeInfo ? [] : [Validators.requiredTrue],
        nonNullable: false
      }),
      commPrefs: new FormGroup<ICommunicationPreferencesFormControls>({
        commPrefsPhone: new FormControl<boolean | null>(
          profile.communicationPreferences?.includes(CommunicationPreference.PHONE),
          { nonNullable: false }
        ),
        commPrefsSms: new FormControl<boolean | null>(
          profile.communicationPreferences?.includes(CommunicationPreference.SMS),
          { nonNullable: false }
        )
      }),
    });

    if (!this.useFullForm) {
      // remove redundant fields that were asked in match flow
      formGroup.removeControl('firstName');
      formGroup.removeControl('lastName');
      formGroup.removeControl('preferredFirstName');
      formGroup.removeControl('timeZone');
      formGroup.removeControl('genderId');
    }

    if (this.accountComplete) {
      formGroup.removeControl('verification');
    }

    // For SAAS invited clients, the country field is required
    // And we enable/disable filling out the rest of the address fields based on whether
    // the country field is filled
    if (this.saasInvited()) {
      formGroup.controls.mailingCountry?.addValidators(Validators.required);
      if (formGroup.controls.mailingCountry.value) {
        this.addressFieldsDisabled.set(false);
        this.setCountrySpecificOptions(formGroup.controls.mailingCountry.value);
      }
    }

    return formGroup;
  }

  private subscribeToCountryFieldUpdates(): void {
    if (!this.saasInvited()) {
      return;
    }

    this.profileForm?.controls.mailingCountry.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroyed$)
    ).subscribe((value) => {
      const country = value;
      if (!country) {
        this.addressFieldsDisabled.set(true);
        this.toggleAddressFields(true);
      } else {
        this.addressFieldsDisabled.set(false);
        this.toggleAddressFields(false);

        this.setCountrySpecificOptions(country);
      }
    });
  }

  private setCountrySpecificOptions(country: string): void {
    if (country === SupportedCountries.AUSTRALIA) {
      this.phoneNumberMask = this.masks['ausPhoneMask'];
      this.zipcodeMask = this.masks['ausZipcodeMask'];
      this.stateOptions.set(AUStateNameMap);
    } else {
      this.phoneNumberMask = this.masks['phoneMask'];
      this.zipcodeMask = this.masks['zipcodeMask'];
      this.stateOptions.set(USStateNameMap);
    }
  }

  /**
  * Wrapper for better unit tests.
  */
  startSubmitting(): void {
    this.submitting = true;
  }

  /**
  * Wrapper for better unit tests.
  */
  stopSubmitting(): void {
    this.submitting = false;
  }

  /**
   * set error to 'base' string if present,
   * deconstruct ServiceError and set to string if no 'base' is present
   * or set generic error in the event that no error returns from the back end
   */
  setErrorMessage(err: ServiceError): void {
    // TODO: (PE-9368) part of investigation to make sure correct error format is being used
    if (err?.base && Array.isArray(err.base)) {
      this.error = err.base.join(', ');
    } else if (err && Object.keys(err).length > 0) {
      const errorKey = Object.keys(err)[0];
      const errorValue = err[errorKey as keyof ServiceError];
      if (Array.isArray(errorValue)) {
        this.error = errorValue.join(', ');
      }
    } else if (this.saasInvited()) {
      // SAAS clients only have access to a support email
      this.error = `Something's not working—please contact us at \
        <a href="mailto:${this.saasSupportEmail}">${this.saasSupportEmail}<a> \
        to continue ${this.accountStatus} your account`;
    } else {
      this.error = `Something's not working—please contact us at \
        <a href='${this.supportPhoneLink}'>${this.supportPhoneDisplay}</a> or \
        <a href="mailto:${this.supportEmail}">${this.supportEmail}<a> \
        to continue ${this.accountStatus} your account`;
    }
  }

  /**
   * if any of these required fields has not been filled prior to arrival
   * on this page, the full form should show. (ex. Provider-invited)
   * otherwise, remove fields redundant from match flow
   * @param profile
   */
  checkEmptyRequiredFields(profile: ClientUserProfile): void {
    const requiredFields = [profile.firstName, profile.lastName, profile.gender];
    this.useFullForm = requiredFields.some((field) => field === null || field === undefined || field === '');
  }

  /**
   * Datepicker has issues on tablet or smaller depending on the device
   * so we want to show the user's bday (if it's already set) in the correct format
   * Format for mobile and desktop display.
   */
  private setBday(): void {
    const birthDateControl = this.profileForm?.controls.birthDate;
    const birthDateValue = birthDateControl?.value;

    if (birthDateValue) {
      if (this.isMobile) {
        const [year, month, day] = birthDateValue.split('-');
        this.mobileBday = `${month}/${day}/${year}`;

        birthDateControl?.setValue(this.mobileBday, { emitEvent: false });
      } else if (this.mobileBday) {
        const [month, day, year] = this.mobileBday.split('/');
        const desktopDate = `${year}-${month}-${day}`;

        birthDateControl?.setValue(desktopDate, { emitEvent: false });
      }
    }
  }

  private toggleAddressFields(shouldDisable: boolean): void {
    if (shouldDisable && this.profileForm) {
      this.profileForm.controls?.mailingAddressLine1?.disable();
      this.profileForm.controls?.mailingAddressLine2?.disable();
      this.profileForm.controls?.mailingCity?.disable();
      this.profileForm.controls?.mailingState?.disable();
      this.profileForm.controls?.mailingPostalCode?.disable();
    } else {
      this.profileForm.controls?.mailingAddressLine1?.enable();
      this.profileForm.controls?.mailingAddressLine2?.enable();
      this.profileForm.controls?.mailingCity?.enable();
      this.profileForm.controls?.mailingState?.enable();
      this.profileForm.controls?.mailingPostalCode?.enable();
    }
  }
}
